import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/common/PageHeader';
import { SectionCard } from '../components/common/SectionCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { Input } from '../components/ui/Input';
import { ScoreChip } from '../components/common/ScoreChip';
import { RecordStatusPill } from '../components/common/StatusPills';
import { usePlatform } from '../contexts/PlatformContext';
import { entities } from '../data/entities';
import { formatAed } from '../utils/format';
import { STAGE_META } from '../utils/stages';
import { cn } from '../utils/cn';
const complianceClasses: Record<string, string> = {
  'good-standing': 'text-uae-leaf',
  'under-review': 'text-uae-bronze',
  restricted: 'text-uae-red'
};
export function Registry() {
  const {
    applications
  } = usePlatform();
  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState(entities[0].id);
  const filtered = entities.filter(entity => entity.name.toLowerCase().includes(query.trim().toLowerCase()));
  const selected = entities.find(entity => entity.id === selectedId) ?? entities[0];
  const history = applications.filter(record => record.entityId === selected.id);
  return <div data-id="element-1495" data-mp="2n97t42l39jly">
      <PageHeader eyebrow="Administration" title="Entities registry" description="Federal, local government, semi-government and private organisers that apply for event support, with their bid history and compliance standing." data-id="element-1496" data-mp="2n9hknuls7ytr" />

      <div className="grid gap-4 p-6 xl:grid-cols-[380px_1fr]" data-id="element-1497" data-mp="28klhaauo79zh">
        <div className="space-y-3" data-id="element-1498" data-mp="28iqt8no3r1r6">
          <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search entities" aria-label="Search entities" data-id="element-1499" data-mp="24k1g8w6z7m82" />
          <div className="space-y-2" data-id="element-1500" data-mp="1ul27zeq4hsll">
            {filtered.map(entity => <button key={entity.id} type="button" onClick={() => setSelectedId(entity.id)} aria-current={selected.id === entity.id} className={cn('w-full rounded-sm border px-3 py-2.5 text-left transition-colors', selected.id === entity.id ? 'border-uae-green bg-uae-green text-white' : 'border-border bg-card hover:bg-muted')} data-id="element-1501" data-mp="1kxzofu3339rp">
                <p className="text-[13px] font-medium leading-snug" data-id="element-1502" data-mp="28nk9ciissh3m">{entity.name}</p>
                <p className={cn('mt-0.5 text-[11px]', selected.id === entity.id ? 'text-white/70' : 'text-muted-foreground')} data-id="element-1503" data-mp="1tyxu0gt67pce">
                  {entity.kind} · {entity.emirate}
                </p>
              </button>)}
            {filtered.length === 0 && <p className="rounded-sm border border-dashed border-border px-3 py-6 text-center text-[12px] text-muted-foreground" data-id="element-1504" data-mp="2rfmtw4yk8hj5">
                No entities match “{query}”.
              </p>}
          </div>
        </div>

        <div className="space-y-4" data-id="element-1505" data-mp="pow2lke67oix">
          <SectionCard title={selected.name} description={`${selected.kind} · ${selected.emirate}`} data-id="element-1506" data-mp="po30iwq5pn74">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1507" data-mp="kqbcidsmul87">
              <div data-id="element-1508" data-mp="3t6e7pk2k6hfn">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1509" data-mp="3t5v0axajbbfv">
                  Bid success rate
                </dt>
                <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-1510" data-mp="60dgjbogor8g">
                  {selected.bidsSubmitted === 0 ? '—' : `${Math.round(selected.bidsWon / selected.bidsSubmitted * 100)}%`}
                </dd>
                <dd className="text-[11px] text-muted-foreground" data-id="element-1511" data-mp="1hdz8t5pmb8y7">
                  {selected.bidsWon} of {selected.bidsSubmitted} bids
                </dd>
              </div>
              <div data-id="element-1512" data-mp="28poubl5jlcxg">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1513" data-mp="28q6ma5l694zk">
                  Support received
                </dt>
                <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-1514" data-mp="2hoinf1jnzl69">
                  {formatAed(selected.supportReceivedAed)}
                </dd>
              </div>
              <div data-id="element-1515" data-mp="2mlsc2ez8bst1">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1516" data-mp="2rnhpg19xcika">
                  Compliance standing
                </dt>
                <dd className={cn('mt-1 text-lg font-semibold capitalize', complianceClasses[selected.compliance])} data-id="element-1517" data-mp="2vn08ssm3kll0">
                  {selected.compliance.replace('-', ' ')}
                </dd>
              </div>
              <div data-id="element-1518" data-mp="3abmmyqzzn7an">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1519" data-mp="3fctv4r61m8hu">
                  Contact
                </dt>
                <dd className="mt-1 text-[13px]" data-id="element-1520" data-mp="13q8fqvynus7g">{selected.contact}</dd>
              </div>
            </dl>
          </SectionCard>

          <SectionCard title="Application history" description="Every record submitted by this entity" bodyClassName="p-0" data-id="element-1521" data-mp="ysgo831xh83s">
            {history.length === 0 ? <p className="px-4 py-8 text-center text-[12px] text-muted-foreground" data-id="element-1522" data-mp="oukgzmc42xay">
                This entity has no applications in the current environment.
              </p> : <div className="overflow-x-auto" data-id="element-1523" data-mp="1hj7vmz2cuofj">
                <Table data-id="element-1524" data-mp="1cp3nichcsacv">
                  <TableHeader data-id="element-1525" data-mp="1coc1ntdk5qzs">
                    <TableRow data-id="element-1526" data-mp="17r223kwgfyy8">
                      <TableHead className="w-[150px]" data-id="element-1527" data-mp="17rtz905pa4qm">Reference</TableHead>
                      <TableHead className="min-w-[220px]" data-id="element-1528" data-mp="25668znv6hg98">Event</TableHead>
                      <TableHead data-id="element-1529" data-mp="258aluvb21pln">Stage</TableHead>
                      <TableHead className="text-right" data-id="element-1530" data-mp="20eohrriyiu1g">Support</TableHead>
                      <TableHead className="text-right" data-id="element-1531" data-mp="3untkuhu877lf">Score</TableHead>
                      <TableHead data-id="element-1532" data-mp="3upxqb47rwent">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-1533" data-mp="3kxrxxtdlzv83">
                    {history.map(record => <TableRow key={record.id} data-id="element-1534" data-mp="3kvnwrj73habi">
                        <TableCell className="font-mono text-[11px] text-muted-foreground" data-id="element-1535" data-mp="3pw38k6bqa9rd">
                          {record.ref}
                        </TableCell>
                        <TableCell data-id="element-1536" data-mp="3fyp65amn3li2">
                          <Link to={`/applications/${record.id}`} className="text-[13px] hover:underline" data-id="element-1537" data-mp="3fzqfnao0bzxo">
                            {record.eventName}
                          </Link>
                        </TableCell>
                        <TableCell className="text-[12px]" data-id="element-1538" data-mp="108knx1dmvvyx">
                          {STAGE_META[record.stage].label}
                        </TableCell>
                        <TableCell className="text-right font-mono text-[12px]" data-id="element-1539" data-mp="2pooetrg2yo30">
                          {formatAed(record.requestedSupportAed)}
                        </TableCell>
                        <TableCell className="text-right" data-id="element-1540" data-mp="2knh8zxxhzyyz">
                          <ScoreChip score={record.aiScore} data-id="element-1541" data-mp="2zf9lm25wqyx0" />
                        </TableCell>
                        <TableCell data-id="element-1542" data-mp="2umq5axfu5poi">
                          <RecordStatusPill status={record.status} data-id="element-1543" data-mp="2ulokw2cxjqu4" />
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>}
          </SectionCard>
        </div>
      </div>
    </div>;
}